
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Tenant } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import { AuthContext } from '../../contexts/AuthContext';
import Spinner from '../../components/common/Spinner';
import ConfirmationModal from '../../components/common/ConfirmationModal';

const TenantModal: React.FC<{
    tenantToEdit: Tenant | null;
    onClose: () => void;
    onSave: (tenantData: Omit<Tenant, 'tenantId' | 'createdAt'>) => Promise<void>;
}> = ({ tenantToEdit, onClose, onSave }) => {
    const [name, setName] = useState(tenantToEdit?.name || '');
    const [logoUrl, setLogoUrl] = useState(tenantToEdit?.settings.branding.logoUrl || '');
    const [primaryColor, setPrimaryColor] = useState(tenantToEdit?.settings.branding.primaryColor || '#3B82F6');
    const [active, setActive] = useState(tenantToEdit?.active ?? true);
    const [maxVideos, setMaxVideos] = useState(tenantToEdit?.settings.maxVideos || 100);
    const [maxUsers, setMaxUsers] = useState(tenantToEdit?.settings.maxUsers || 50);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isEditing = !!tenantToEdit;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const tenantData: Omit<Tenant, 'tenantId' | 'createdAt'> = {
                name,
                active,
                settings: {
                    branding: { logoUrl, primaryColor },
                    maxVideos,
                    maxUsers
                }
            };
            await onSave(tenantData);
            onClose();
        } catch (error) {
            console.error("Error saving tenant:", error);
            alert("Error al guardar la agencia.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex justify-center items-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl p-8 w-full max-w-lg animate-fade-in-scale">
                <h2 className="text-2xl font-bold text-white mb-6">{isEditing ? 'Editar Agencia' : 'Nueva Agencia / Inquilino'}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Nombre de la Agencia</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white outline-none focus:ring-2 focus:ring-blue-500" required placeholder="Ej: Agencia de Marketing Madrid" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">URL del Logo de la Agencia (Software Personalizado)</label>
                        <input type="url" value={logoUrl} onChange={e => setLogoUrl(e.target.value)} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white outline-none focus:ring-2 focus:ring-blue-500" placeholder="https://..." />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Color de Marca</label>
                            <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-full h-10 p-1 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer" />
                        </div>
                        <div className="flex items-center gap-2 pt-6">
                            <input type="checkbox" id="tenant-active" checked={active} onChange={e => setActive(e.target.checked)} className="w-5 h-5 bg-gray-800 border-gray-700 rounded" />
                            <label htmlFor="tenant-active" className="text-sm text-gray-300">Software Activo</label>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 pt-4 mt-4 grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Límite de Videos</label>
                            <input type="number" value={maxVideos} onChange={e => setMaxVideos(parseInt(e.target.value))} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">Límite de Usuarios</label>
                            <input type="number" value={maxUsers} onChange={e => setMaxUsers(parseInt(e.target.value))} className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white" />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-6">
                        <button type="button" onClick={onClose} className="px-5 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">Cancelar</button>
                        <button type="submit" disabled={isSubmitting} className="px-5 py-2 bg-blue-600 text-white rounded-lg font-bold shadow-lg shadow-blue-900/20">
                            {isSubmitting ? <Spinner size="sm" /> : (isEditing ? 'Guardar Configuración' : 'Crear Inquilino')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const TenantsPage: React.FC = () => {
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
    const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchTenants = useCallback(async () => {
        setLoading(true);
        try {
            const data = await firestoreService.getTenants();
            setTenants(data);
        } catch (error) {
            console.error("Error fetching tenants:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTenants();
    }, [fetchTenants]);

    const handleOpenModal = (tenant: Tenant | null) => {
        setEditingTenant(tenant);
        setIsModalOpen(true);
    };

    const handleSaveTenant = async (tenantData: Omit<Tenant, 'tenantId' | 'createdAt'>) => {
        if (editingTenant) {
            await firestoreService.updateTenant(editingTenant.tenantId, tenantData);
        } else {
            await firestoreService.addTenant(tenantData);
        }
        fetchTenants();
    };

    const confirmDeleteTenant = async () => {
        if (!tenantToDelete) return;
        setIsDeleting(true);
        try {
            await firestoreService.deleteTenant(tenantToDelete.tenantId);
            fetchTenants();
            setTenantToDelete(null);
        } catch (error) {
            alert("Error al eliminar la agencia.");
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="container mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white">Inquilinos de la Plataforma (Agencias)</h1>
                    <p className="text-gray-400">Desde aquí controlas quién tiene acceso a tu software.</p>
                </div>
                <button 
                    onClick={() => handleOpenModal(null)}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold shadow-lg hover:bg-blue-500 transition-all transform hover:scale-105">
                    + Nueva Agencia
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {loading ? (
                    <div className="col-span-full py-20 text-center"><Spinner size="lg" /></div>
                ) : tenants.length > 0 ? (
                    tenants.map((tenant) => (
                        <div key={tenant.tenantId} className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-blue-500/50 transition-all shadow-xl flex flex-col group">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center border border-gray-700 overflow-hidden" style={{borderColor: tenant.settings.branding.primaryColor + '44'}}>
                                        {tenant.settings.branding.logoUrl ? (
                                            <img src={tenant.settings.branding.logoUrl} className="w-full h-full object-contain" />
                                        ) : (
                                            <span className="text-xl font-bold" style={{color: tenant.settings.branding.primaryColor}}>{tenant.name.charAt(0)}</span>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-white font-bold text-lg">{tenant.name}</h3>
                                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${tenant.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                            {tenant.active ? 'Software Activo' : 'Suspendido'}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleOpenModal(tenant)} className="p-2 bg-gray-800 rounded-lg hover:bg-blue-600 text-white transition-colors" title="Editar"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg></button>
                                    <button onClick={() => setTenantToDelete(tenant)} className="p-2 bg-gray-800 rounded-lg hover:bg-red-600 text-white transition-colors" title="Eliminar"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                </div>
                            </div>
                            
                            <div className="space-y-3 mt-4">
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-500 italic">ID del Inquilino:</span>
                                    <span className="text-gray-400 font-mono">{tenant.tenantId}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Cuota de Videos</span>
                                    <span className="text-white font-bold">{tenant.settings.maxVideos}</span>
                                </div>
                                <div className="w-full bg-gray-800 h-1.5 rounded-full overflow-hidden">
                                    <div className="bg-blue-600 h-full w-[0%]"></div>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-400">Usuarios Permitidos</span>
                                    <span className="text-white font-bold">{tenant.settings.maxUsers}</span>
                                </div>
                            </div>

                            <div className="mt-6 pt-6 border-t border-gray-800 flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{backgroundColor: tenant.settings.branding.primaryColor}}></div>
                                    <span className="text-xs text-gray-500">Color de Marca</span>
                                </div>
                                <span className="text-[10px] text-gray-600 font-mono">{tenant.settings.branding.primaryColor}</span>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="col-span-full p-20 text-center bg-gray-900 rounded-xl border border-dashed border-gray-700">
                        <p className="text-gray-500 italic">No hay inquilinos registrados. Empieza creando tu primera Agencia.</p>
                    </div>
                )}
            </div>

            {isModalOpen && <TenantModal tenantToEdit={editingTenant} onClose={() => setIsModalOpen(false)} onSave={handleSaveTenant} />}
            <ConfirmationModal isOpen={!!tenantToDelete} onClose={() => setTenantToDelete(null)} onConfirm={confirmDeleteTenant} title="Eliminar Inquilino" message={<>¿Estás seguro de eliminar permanentemente a <strong>{tenantToDelete?.name}</strong>? Se borrará el acceso a su software y todos sus datos vinculados.</>} isConfirming={isDeleting} />
        </div>
    );
};

export default TenantsPage;
